import { HealthBadge } from '@/components/admin/HealthBadge';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  DASHBOARD_DIRECTION_PRESETS,
  DASHBOARD_INTENT_PRESETS,
  type DashboardDirection,
  type DashboardIntent,
  type HealthCheck,
  type SystemInfoStorage,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Lightbulb, Pencil } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';

export function AdminDashboard() {
  const infoQuery = useQuery({
    queryKey: ['admin', 'system-info'],
    queryFn: () => api.adminSystemInfo(),
  });

  const healthQuery = useQuery({
    queryKey: ['admin', 'system-health'],
    queryFn: () => api.adminSystemHealth(),
    refetchInterval: 15_000,
  });

  const health = healthQuery.data;
  const healthLoading = healthQuery.isLoading;

  return (
    <PageShell
      header={
        <PageHeader
          title="대시보드"
          description="현재 인스턴스의 시스템 구성 정보입니다. 변경은 관리 메뉴에서 진행합니다."
        />
      }
    >
      {infoQuery.isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : infoQuery.error ? (
        <p className="text-sm text-destructive">
          {infoQuery.error instanceof ApiError
            ? infoQuery.error.message
            : '시스템 정보를 불러오지 못했습니다.'}
        </p>
      ) : infoQuery.data ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Card title="시스템 DB" badge={pickBadge(health?.db, healthLoading)}>
            <Row k="호스트" v={`${infoQuery.data.db.host}:${infoQuery.data.db.port}`} />
            <Row k="데이터베이스" v={infoQuery.data.db.database} mono />
            <Row k="사용자" v={infoQuery.data.db.username} mono />
          </Card>

          <StorageCard
            storage={infoQuery.data.storage}
            badge={pickBadge(health?.storage, healthLoading)}
          />

          <Card title="기본 LLM" badge={pickBadge(health?.llm, healthLoading)}>
            {infoQuery.data.llmDefault ? (
              infoQuery.data.llmDefault.kind === 'endpoint' ? (
                <>
                  <Row
                    k="이름"
                    v={infoQuery.data.llmDefault.displayName ?? infoQuery.data.llmDefault.name}
                  />
                  <Row k="모델" v={infoQuery.data.llmDefault.model} mono />
                  <Row k="Base URL" v={infoQuery.data.llmDefault.baseUrl} mono />
                </>
              ) : (
                <>
                  <Row
                    k="이름"
                    v={infoQuery.data.llmDefault.displayName ?? infoQuery.data.llmDefault.name}
                  />
                  <Row k="종류" v="본딩 (랜덤 분산)" />
                  <Row k="멤버 수" v={`${infoQuery.data.llmDefault.memberCount}개`} />
                </>
              )
            ) : (
              <p className="text-sm text-foreground-muted">기본 LLM 이 설정되지 않았습니다.</p>
            )}
          </Card>
        </div>
      ) : null}

      {health ? (
        <p className="mt-4 text-xs text-foreground-muted">
          마지막 점검: {formatCheckedAt(health.checkedAt)} · 15초마다 자동 갱신
        </p>
      ) : null}

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <LessonsPendingCard />
        <ToolErrorsCard />
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <ChatSettingsCard />
        <PersonaCard />
      </div>

      <div className="mt-8">
        <GuardrailsCard />
      </div>

      <div className="mt-8">
        <DashboardDesignCard />
      </div>
    </PageShell>
  );
}

/**
 * 최근 24시간 동안 발생한 skill 도구 / artifact RPC 실패 카운터.
 * 클릭 시 /admin/audit?status=error deep link 로 이동.
 */
function ToolErrorsCard() {
  const navigate = useNavigate();
  const fromIso = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'audit-logs', 'tool-errors-24h'],
    queryFn: () => api.adminAuditLogs({ status: 'error', from: fromIso, limit: 100 }),
    refetchInterval: 30_000,
  });
  const count = data?.total ?? 0;
  const goAudit = () =>
    navigate({ to: '/admin/audit', search: { status: 'error', action: undefined } });
  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <AlertTriangle
            className={`size-5 ${count > 0 ? 'text-destructive' : 'text-foreground-muted'}`}
          />
          <h2 className="text-lg font-semibold">도구 / 대시보드 오류</h2>
        </div>
        <Button variant="secondary" onClick={goAudit}>
          오류만 보기
        </Button>
      </header>
      <p className="text-sm text-foreground-muted">
        최근 24시간 동안 SQL 저장 / 실행 / 대시보드 데이터 호출에서 실패한 건수입니다.
      </p>
      <div className="mt-4 flex items-baseline gap-3">
        <span
          className={`text-3xl font-semibold tabular-nums ${count > 0 ? 'text-destructive' : ''}`}
        >
          {isLoading ? '…' : count}
        </span>
        <span className="text-sm text-foreground-muted">건 (24h)</span>
      </div>
    </section>
  );
}

function StorageCard({
  storage,
  badge,
}: {
  storage: SystemInfoStorage | null;
  badge: ReactNode;
}) {
  const navigate = useNavigate();
  const goManage = () => navigate({ to: '/admin/file-storage' });

  return (
    <section className="group bg-surface border border-border rounded-2xl p-6">
      <header className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">파일 저장소</h2>
        <div className="flex items-center gap-2">
          {badge}
          <button
            type="button"
            aria-label="파일 저장소 설정으로 이동"
            onClick={goManage}
            className="size-7 grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
          >
            <Pencil className="size-3.5" />
          </button>
        </div>
      </header>
      <dl className="space-y-2.5">
        {storage ? (
          storage.type === 's3' ? (
            <>
              <Row k="종류" v="S3 호환 객체 스토리지" />
              <Row k="엔드포인트" v={storage.endpoint} mono />
              <Row k="버킷" v={storage.bucket} mono />
              <Row k="리전" v={storage.region} mono />
            </>
          ) : (
            <>
              <Row k="종류" v={storage.type === 'nfs' ? 'NFS 마운트' : 'Local 파일시스템'} />
              <Row k="경로" v={storage.rootPath} mono />
            </>
          )
        ) : (
          <div className="flex items-center gap-3">
            <p className="text-sm text-foreground-muted">
              구성되지 않음 — 설정하면 파일 업로드가 활성화됩니다.
            </p>
            <Button type="button" variant="secondary" onClick={goManage}>
              설정하기
            </Button>
          </div>
        )}
      </dl>
    </section>
  );
}

function LessonsPendingCard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'skill-lessons', 'pending-count'],
    queryFn: () => api.adminSkillLessonPendingCount(),
    refetchInterval: 30_000,
  });
  const count = data?.count ?? 0;
  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="size-5 text-[hsl(30_90%_50%)]" />
          <h2 className="text-lg font-semibold">경험에서 배운 점</h2>
        </div>
        <Button variant="secondary" onClick={() => navigate({ to: '/admin/skill-lessons' })}>
          목록 열기
        </Button>
      </header>
      <p className="text-sm text-foreground-muted">
        Tool 호출 실패가 자동 수집됩니다. 검토 후 활성화하면 다음 세션부터 해당 Skill 에 주입됩니다.
      </p>
      <div className="mt-4 flex items-baseline gap-3">
        <span className="text-3xl font-semibold tabular-nums">{isLoading ? '…' : count}</span>
        <span className="text-sm text-foreground-muted">건 검토 대기</span>
      </div>
    </section>
  );
}

function ChatSettingsCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.adminSettings(),
  });

  const [toolIterationLimit, setToolIterationLimit] = useState<string>('');
  const [toolIterationLimitMulti, setToolIterationLimitMulti] = useState<string>('');
  const [excelIngestConcurrency, setExcelIngestConcurrency] = useState<string>('');
  const [excelLlmStructure, setExcelLlmStructure] = useState<boolean>(false);
  const [excelLlmTopN, setExcelLlmTopN] = useState<string>('');
  const [excelLlmMaxCol, setExcelLlmMaxCol] = useState<string>('');
  const [excelLlmRetries, setExcelLlmRetries] = useState<string>('');
  const [showResponseTime, setShowResponseTime] = useState<boolean>(false);
  const [wikiEnabled, setWikiEnabled] = useState<boolean>(true);
  const [userModelSelectionEnabled, setUserModelSelectionEnabled] = useState<boolean>(true);
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState<string>('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (data) {
      setToolIterationLimit(String(data.toolIterationLimit));
      setToolIterationLimitMulti(String(data.toolIterationLimitMulti));
      setExcelIngestConcurrency(String(data.excelIngestConcurrency));
      setExcelLlmStructure(data.excelLlmStructure);
      setExcelLlmTopN(String(data.excelLlmTopN));
      setExcelLlmMaxCol(String(data.excelLlmMaxCol));
      setExcelLlmRetries(String(data.excelLlmRetries));
      setShowResponseTime(data.showResponseTime);
      setWikiEnabled(data.wikiEnabled);
      setUserModelSelectionEnabled(data.userModelSelectionEnabled);
      setIdleTimeoutMinutes(String(data.idleTimeoutMinutes));
    }
  }, [data]);

  const updateMut = useMutation({
    mutationFn: (body: {
      toolIterationLimit?: number;
      toolIterationLimitMulti?: number;
      excelIngestConcurrency?: number;
      excelLlmStructure?: boolean;
      excelLlmTopN?: number;
      excelLlmMaxCol?: number;
      excelLlmRetries?: number;
      showResponseTime?: boolean;
      wikiEnabled?: boolean;
      userModelSelectionEnabled?: boolean;
      idleTimeoutMinutes?: number;
    }) => api.adminUpdateSettings(body),
    onSuccess: (res) => {
      setServerError(null);
      setSavedAt(Date.now());
      qc.setQueryData(['admin', 'settings'], res);
      // 사용자 챗 UI 가 즉시 반영하도록 공개 설정 캐시도 무효화.
      qc.invalidateQueries({ queryKey: ['app', 'settings'] });
    },
    onError: (err: unknown) => {
      setSavedAt(null);
      setServerError(err instanceof ApiError ? err.message : '저장에 실패했습니다.');
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const n = Number.parseInt(toolIterationLimit, 10);
    if (!Number.isFinite(n) || n < 1 || n > 50) {
      setServerError('툴 호출 한도는 1 ~ 50 사이의 정수여야 합니다.');
      return;
    }
    const nMulti = Number.parseInt(toolIterationLimitMulti, 10);
    if (!Number.isFinite(nMulti) || nMulti < 1 || nMulti > 50) {
      setServerError('cross-domain 한도는 1 ~ 50 사이의 정수여야 합니다.');
      return;
    }
    const c = Number.parseInt(excelIngestConcurrency, 10);
    if (!Number.isFinite(c) || c < 1 || c > 20) {
      setServerError('엑셀 동시 처리 갯수는 1 ~ 20 사이의 정수여야 합니다.');
      return;
    }
    const idle = Number.parseInt(idleTimeoutMinutes, 10);
    if (!Number.isFinite(idle) || idle < 0 || idle > 1440) {
      setServerError('자동 로그아웃 시간은 0 ~ 1440 사이의 정수여야 합니다.');
      return;
    }
    const topN = Number.parseInt(excelLlmTopN, 10);
    if (!Number.isFinite(topN) || topN < 10 || topN > 300) {
      setServerError('엑셀 구조 인식 행 수는 10 ~ 300 사이의 정수여야 합니다.');
      return;
    }
    const maxCol = Number.parseInt(excelLlmMaxCol, 10);
    if (!Number.isFinite(maxCol) || maxCol < 4 || maxCol > 120) {
      setServerError('엑셀 구조 인식 열 수는 4 ~ 120 사이의 정수여야 합니다.');
      return;
    }
    const retries = Number.parseInt(excelLlmRetries, 10);
    if (!Number.isFinite(retries) || retries < 1 || retries > 5) {
      setServerError('엑셀 구조 인식 재시도는 1 ~ 5 사이의 정수여야 합니다.');
      return;
    }
    updateMut.mutate({
      toolIterationLimit: n,
      toolIterationLimitMulti: nMulti,
      excelIngestConcurrency: c,
      excelLlmStructure,
      excelLlmTopN: topN,
      excelLlmMaxCol: maxCol,
      excelLlmRetries: retries,
      showResponseTime,
      wikiEnabled,
      userModelSelectionEnabled,
      idleTimeoutMinutes: idle,
    });
  };

  const dirty = data
    ? String(data.toolIterationLimit) !== toolIterationLimit ||
      String(data.toolIterationLimitMulti) !== toolIterationLimitMulti ||
      String(data.excelIngestConcurrency) !== excelIngestConcurrency ||
      data.excelLlmStructure !== excelLlmStructure ||
      String(data.excelLlmTopN) !== excelLlmTopN ||
      String(data.excelLlmMaxCol) !== excelLlmMaxCol ||
      String(data.excelLlmRetries) !== excelLlmRetries ||
      data.showResponseTime !== showResponseTime ||
      data.wikiEnabled !== wikiEnabled ||
      data.userModelSelectionEnabled !== userModelSelectionEnabled ||
      String(data.idleTimeoutMinutes) !== idleTimeoutMinutes
    : false;

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">채팅 설정</h2>
        <p className="text-sm text-foreground-muted mt-1">
          한 번의 대화 턴에서 허용할 툴 호출 횟수 상한과 엑셀 첨부 분석의 동시 처리 갯수입니다.
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '설정을 불러오지 못했습니다.'}
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4 max-w-md">
          <Field
            label="툴 호출 한도"
            htmlFor="toolIterationLimit"
            hint="단일 skill 세션 기본 한도. 1 ~ 50 사이."
          >
            <Input
              id="toolIterationLimit"
              type="number"
              min={1}
              max={50}
              step={1}
              value={toolIterationLimit}
              onChange={(e) => setToolIterationLimit(e.currentTarget.value)}
            />
          </Field>

          <Field
            label="cross-domain 한도"
            htmlFor="toolIterationLimitMulti"
            hint="활성 skill 이 2개 이상인 세션에서만 사용. 보통 기본값보다 크게. 1 ~ 50 사이, 권장 15."
          >
            <Input
              id="toolIterationLimitMulti"
              type="number"
              min={1}
              max={50}
              step={1}
              value={toolIterationLimitMulti}
              onChange={(e) => setToolIterationLimitMulti(e.currentTarget.value)}
            />
          </Field>

          <Field
            label="엑셀 동시 처리 갯수"
            htmlFor="excelIngestConcurrency"
            hint="한 챗 메시지에서 (파일×시트) 작업을 동시에 처리할 상한. 1 ~ 20 사이의 정수, 기본값 5."
          >
            <Input
              id="excelIngestConcurrency"
              type="number"
              min={1}
              max={20}
              step={1}
              value={excelIngestConcurrency}
              onChange={(e) => setExcelIngestConcurrency(e.currentTarget.value)}
            />
          </Field>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              id="excelLlmStructure"
              type="checkbox"
              className="mt-1 size-4 accent-accent"
              checked={excelLlmStructure}
              onChange={(e) => setExcelLlmStructure(e.currentTarget.checked)}
            />
            <span className="block">
              <span className="block text-sm font-medium">엑셀 LLM 구조 인식</span>
              <span className="block text-xs text-foreground-muted mt-0.5">
                여러 표가 쌓이거나 좌우로 배치된 한국식 대시보드 시트를 LLM 이 인식해 표·헤더를
                분리합니다. 시트당 LLM 호출 비용/지연이 있어 기본은 꺼져 있습니다. 끄면 기존
                휴리스틱 방식으로 동작합니다.
              </span>
            </span>
          </label>

          {excelLlmStructure && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pl-7">
              <Field
                label="상단 행 수"
                htmlFor="excelLlmTopN"
                hint="LLM 에 보여줄 시트 상단 행 수 (10~300). 데이터 적재 행과 무관."
              >
                <Input
                  id="excelLlmTopN"
                  type="number"
                  min={10}
                  max={300}
                  step={1}
                  value={excelLlmTopN}
                  onChange={(e) => setExcelLlmTopN(e.currentTarget.value)}
                />
              </Field>
              <Field
                label="최대 열 수"
                htmlFor="excelLlmMaxCol"
                hint="LLM 에 보여줄 최대 열 (4~120). 실제 적재 열은 코드가 끝까지 스캔하므로 잘리지 않음."
              >
                <Input
                  id="excelLlmMaxCol"
                  type="number"
                  min={4}
                  max={120}
                  step={1}
                  value={excelLlmMaxCol}
                  onChange={(e) => setExcelLlmMaxCol(e.currentTarget.value)}
                />
              </Field>
              <Field
                label="재시도 횟수"
                htmlFor="excelLlmRetries"
                hint="검증 실패 시 콕 집은 피드백으로 다시 시도할 최대 횟수 (1~5)."
              >
                <Input
                  id="excelLlmRetries"
                  type="number"
                  min={1}
                  max={5}
                  step={1}
                  value={excelLlmRetries}
                  onChange={(e) => setExcelLlmRetries(e.currentTarget.value)}
                />
              </Field>
            </div>
          )}

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              id="showResponseTime"
              type="checkbox"
              className="mt-1 size-4 accent-accent"
              checked={showResponseTime}
              onChange={(e) => setShowResponseTime(e.currentTarget.checked)}
            />
            <span className="block">
              <span className="block text-sm font-medium">응답 시간 표시</span>
              <span className="block text-xs text-foreground-muted mt-0.5">
                채팅에서 어시스턴트 답변 옆에 응답 소요 시간을 표시합니다. 진행 중에는 실시간
                카운트가 올라가고, 완료 후에는 최종 시간이 메시지에 남습니다.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              id="wikiEnabled"
              type="checkbox"
              className="mt-1 size-4 accent-accent"
              checked={wikiEnabled}
              onChange={(e) => setWikiEnabled(e.currentTarget.checked)}
            />
            <span className="block">
              <span className="block text-sm font-medium">지식 위키 메뉴 노출</span>
              <span className="block text-xs text-foreground-muted mt-0.5">
                끄면 사용자 화면 사이드바에서 '지식 위키' 메뉴가 사라집니다. 어드민 메뉴는 그대로
                유지됩니다.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              id="userModelSelectionEnabled"
              type="checkbox"
              className="mt-1 size-4 accent-accent"
              checked={userModelSelectionEnabled}
              onChange={(e) => setUserModelSelectionEnabled(e.currentTarget.checked)}
            />
            <span className="block">
              <span className="block text-sm font-medium">사용자 모델 선택 허용</span>
              <span className="block text-xs text-foreground-muted mt-0.5">
                끄면 채팅 화면에서 모델 선택 버튼이 사라지고, 사용자는 기본 모델만 사용하게 됩니다.
              </span>
            </span>
          </label>

          <Field
            label="자동 로그아웃 (분)"
            htmlFor="idleTimeoutMinutes"
            hint="마지막 활동 이후 이 시간 동안 추가 활동이 없으면 자동 로그아웃. 0 = 비활성화 (최대 24시간 유지). 1 ~ 1440 사이의 정수."
          >
            <Input
              id="idleTimeoutMinutes"
              type="number"
              min={0}
              max={1440}
              step={1}
              value={idleTimeoutMinutes}
              onChange={(e) => setIdleTimeoutMinutes(e.currentTarget.value)}
            />
          </Field>

          {serverError ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {serverError}
            </div>
          ) : null}

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={!dirty || updateMut.isPending}>
              {updateMut.isPending ? '저장 중…' : '저장'}
            </Button>
            {savedAt ? (
              <span className="text-xs text-foreground-muted">
                저장됨 · {new Date(savedAt).toLocaleTimeString('ko-KR')}
              </span>
            ) : null}
          </div>
        </form>
      )}
    </section>
  );
}

function GuardrailsCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.adminSettings(),
  });

  const [bannedEnforce, setBannedEnforce] = useState(false);
  const [bannedRefusal, setBannedRefusal] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (data) {
      setBannedEnforce(data.bannedWordsEnforce);
      setBannedRefusal(data.bannedWordsRefusalText);
    }
  }, [data]);

  const updateMut = useMutation({
    mutationFn: (body: {
      bannedWordsEnforce: boolean;
      bannedWordsRefusalText: string;
    }) => api.adminUpdateSettings(body),
    onSuccess: (res) => {
      setServerError(null);
      setSavedAt(Date.now());
      qc.setQueryData(['admin', 'settings'], res);
    },
    onError: (err: unknown) => {
      setSavedAt(null);
      setServerError(err instanceof ApiError ? err.message : '저장에 실패했습니다.');
    },
  });

  const dirty = data
    ? data.bannedWordsEnforce !== bannedEnforce || data.bannedWordsRefusalText !== bannedRefusal
    : false;

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">금칙어</h2>
        <p className="text-sm text-foreground-muted mt-1">
          욕설·금칙어가 포함된 입력을 차단·탐지합니다. 일반 지식·업무 답변에는 영향이 없습니다.
          정체성/메타 질문("몇 살이야?", "클로드로 만들었니?") 응답 지침은{' '}
          <a href="/admin/system-prompts" className="text-accent underline">
            시스템 프롬프트
          </a>{' '}
          의 "채팅 — 정체성/메타 질문 응답 지침" 에서 편집합니다.
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '설정을 불러오지 못했습니다.'}
        </p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateMut.mutate({
              bannedWordsEnforce: bannedEnforce,
              bannedWordsRefusalText: bannedRefusal,
            });
          }}
          className="space-y-4"
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 size-4 accent-accent"
              checked={bannedEnforce}
              onChange={(e) => setBannedEnforce(e.currentTarget.checked)}
            />
            <span className="block">
              <span className="block text-sm font-medium">금칙어 차단 적용</span>
              <span className="block text-xs text-foreground-muted mt-0.5">
                켜면 'block' 금칙어가 포함된 입력을 LLM 호출 없이 거부합니다. 꺼도 탐지는 계속
                동작하며, 채팅 로그에서 표시됩니다. 금칙어 목록은{' '}
                <a href="/admin/banned-keywords" className="text-accent underline">
                  금칙어 관리
                </a>{' '}
                에서 등록합니다.
              </span>
            </span>
          </label>

          <Field
            label="차단 거부 문구"
            htmlFor="bannedRefusal"
            hint="비워두면 내장 기본 문구를 사용합니다."
          >
            <textarea
              id="bannedRefusal"
              rows={2}
              maxLength={2000}
              value={bannedRefusal}
              onChange={(e) => setBannedRefusal(e.currentTarget.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
            />
          </Field>

          {serverError ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {serverError}
            </div>
          ) : null}

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={!dirty || updateMut.isPending}>
              {updateMut.isPending ? '저장 중…' : '저장'}
            </Button>
            {savedAt ? (
              <span className="text-xs text-foreground-muted">
                저장됨 · {new Date(savedAt).toLocaleTimeString('ko-KR')}
              </span>
            ) : null}
          </div>
        </form>
      )}
    </section>
  );
}

function PersonaCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.adminSettings(),
  });

  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (data) {
      setName(data.personaName);
      setDescription(data.personaDescription);
    }
  }, [data]);

  const updateMut = useMutation({
    mutationFn: (body: { personaName: string; personaDescription: string }) =>
      api.adminUpdateSettings(body),
    onSuccess: (res) => {
      setServerError(null);
      setSavedAt(Date.now());
      qc.setQueryData(['admin', 'settings'], res);
      qc.invalidateQueries({ queryKey: ['app', 'settings'] });
      qc.invalidateQueries({ queryKey: ['wiki'] });
    },
    onError: (err: unknown) => {
      setSavedAt(null);
      setServerError(err instanceof ApiError ? err.message : '저장에 실패했습니다.');
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedDesc = description.trim();
    if (trimmedName.length < 1 || trimmedName.length > 40) {
      setServerError('페르소나 이름은 1 ~ 40자 사이여야 합니다.');
      return;
    }
    if (trimmedDesc.length < 1 || trimmedDesc.length > 200) {
      setServerError('성격 설명은 1 ~ 200자 사이여야 합니다.');
      return;
    }
    if (data && trimmedName !== data.personaName) {
      const ok = window.confirm(
        `페르소나 이름을 "${data.personaName}" → "${trimmedName}" 으로 바꾸면 위키 중심 페이지가 새 슬러그로 마이그레이션되고, 모든 페이지의 [[${data.personaName}]] 가 [[${trimmedName}]] 로 자동 치환됩니다. 계속하시겠습니까?`,
      );
      if (!ok) return;
    }
    updateMut.mutate({ personaName: trimmedName, personaDescription: trimmedDesc });
  };

  const dirty = data
    ? data.personaName !== name.trim() || data.personaDescription !== description.trim()
    : false;

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">어시스턴트 페르소나</h2>
        <p className="text-sm text-foreground-muted mt-1">
          모든 챗 응답의 자기소개와 위키 중심(center) 페이지의 슬러그/제목으로 사용됩니다. 이름이
          바뀌면 위키 중심 페이지가 자동으로 새 슬러그로 마이그레이션됩니다.
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '설정을 불러오지 못했습니다.'}
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4 max-w-md">
          <Field
            label="이름"
            htmlFor="personaName"
            hint="예: 김부장 / 인호매니저 / 박과장. 챗에서 자기소개 시 사용."
          >
            <Input
              id="personaName"
              type="text"
              maxLength={40}
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
          </Field>

          <Field
            label="성격/말투"
            htmlFor="personaDescription"
            hint="간단한 한 두 문장. system prompt 의 페르소나 블록에만 반영되고 사용자에게는 보이지 않습니다."
          >
            <textarea
              id="personaDescription"
              rows={2}
              maxLength={200}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
            />
          </Field>

          {serverError ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {serverError}
            </div>
          ) : null}

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={!dirty || updateMut.isPending}>
              {updateMut.isPending ? '저장 중…' : '저장'}
            </Button>
            {savedAt ? (
              <span className="text-xs text-foreground-muted">
                저장됨 · {new Date(savedAt).toLocaleTimeString('ko-KR')}
              </span>
            ) : null}
          </div>
        </form>
      )}
    </section>
  );
}

function pickBadge(check: HealthCheck | undefined, loading: boolean): ReactNode {
  return <HealthBadge data={check} isLoading={loading} />;
}

function formatCheckedAt(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString('ko-KR');
  } catch {
    return iso;
  }
}

function Card({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">{title}</h2>
        {badge}
      </header>
      <dl className="space-y-2.5">{children}</dl>
    </section>
  );
}

function Row({ k, v, mono = false }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] items-baseline gap-3 text-sm">
      <dt className="text-foreground-muted">{k}</dt>
      <dd className={mono ? 'font-mono text-xs break-all' : ''}>{v}</dd>
    </div>
  );
}

/**
 * 대시보드 생성 시 서버가 사용자에게 "어떤 톤?" 같은 모달을 띄우지 않고 자동으로 적용할
 * 기본 비주얼 디렉션 (5종) 과 설계 방향 (4종) 을 관리자가 미리 선택.
 *
 * 우선순위: LLM 이 spec 에 명시 > 직전 dashboard artifact 의 메타 > 여기서 고른 기본값.
 */
function DashboardDesignCard() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.adminSettings(),
  });

  const [direction, setDirection] = useState<DashboardDirection>('modern-minimal');
  const [intent, setIntent] = useState<DashboardIntent>('executive');
  const [serverError, setServerError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (data) {
      setDirection(data.dashboardDefaultDirection);
      setIntent(data.dashboardDefaultIntent);
    }
  }, [data]);

  const updateMut = useMutation({
    mutationFn: (body: {
      dashboardDefaultDirection: DashboardDirection;
      dashboardDefaultIntent: DashboardIntent;
    }) => api.adminUpdateSettings(body),
    onSuccess: (res) => {
      setServerError(null);
      setSavedAt(Date.now());
      qc.setQueryData(['admin', 'settings'], res);
    },
    onError: (err: unknown) => {
      setSavedAt(null);
      setServerError(err instanceof ApiError ? err.message : '저장에 실패했습니다.');
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    updateMut.mutate({
      dashboardDefaultDirection: direction,
      dashboardDefaultIntent: intent,
    });
  };

  const dirty = data
    ? data.dashboardDefaultDirection !== direction || data.dashboardDefaultIntent !== intent
    : false;

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">대시보드 디자인 선택</h2>
        <p className="text-sm text-foreground-muted mt-1">
          사용자가 채팅으로 대시보드를 생성할 때 자동으로 적용할 기본 비주얼 디렉션과 설계
          방향입니다. 서버는 더 이상 디자인 톤을 사용자에게 묻지 않습니다. LLM 이 색상을 명시하거나
          직전 대시보드가 있으면 그 값이 우선합니다.
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof ApiError ? error.message : '설정을 불러오지 못했습니다.'}
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-6">
          <fieldset>
            <legend className="text-sm font-medium mb-2">비주얼 디렉션</legend>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {DASHBOARD_DIRECTION_PRESETS.map((preset) => {
                const selected = preset.id === direction;
                return (
                  <label
                    key={preset.id}
                    className={cn(
                      'flex flex-col gap-2 rounded-xl border p-3 cursor-pointer transition',
                      selected
                        ? 'border-accent ring-2 ring-accent/30 bg-accent/5'
                        : 'border-border hover:border-foreground-muted',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="dashboard-default-direction"
                        value={preset.id}
                        checked={selected}
                        onChange={() => setDirection(preset.id)}
                        className="size-4 accent-accent"
                      />
                      <span className="text-sm font-medium">{preset.label}</span>
                    </div>
                    <div
                      className="flex h-6 overflow-hidden rounded-md border border-border"
                      aria-hidden
                    >
                      {preset.swatch.map((color, idx) => (
                        <span
                          key={`${preset.id}-${idx}`}
                          className="flex-1"
                          style={{ background: color }}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-foreground-muted leading-snug">{preset.hint}</p>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-sm font-medium mb-2">설계 방향 (intent)</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {DASHBOARD_INTENT_PRESETS.map((preset) => {
                const selected = preset.id === intent;
                return (
                  <label
                    key={preset.id}
                    className={cn(
                      'flex items-start gap-2 rounded-xl border p-3 cursor-pointer transition',
                      selected
                        ? 'border-accent ring-2 ring-accent/30 bg-accent/5'
                        : 'border-border hover:border-foreground-muted',
                    )}
                  >
                    <input
                      type="radio"
                      name="dashboard-default-intent"
                      value={preset.id}
                      checked={selected}
                      onChange={() => setIntent(preset.id)}
                      className="mt-0.5 size-4 accent-accent"
                    />
                    <span className="block">
                      <span className="block text-sm font-medium">{preset.label}</span>
                      <span className="block text-xs text-foreground-muted mt-0.5 leading-snug">
                        {preset.hint}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          {serverError ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {serverError}
            </div>
          ) : null}

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={!dirty || updateMut.isPending}>
              {updateMut.isPending ? '저장 중…' : '저장'}
            </Button>
            {savedAt ? (
              <span className="text-xs text-foreground-muted">
                저장됨 · {new Date(savedAt).toLocaleTimeString('ko-KR')}
              </span>
            ) : null}
          </div>
        </form>
      )}
    </section>
  );
}
